import { useState, useCallback } from 'react';
import type { ToastType } from '@/components/ui/Toast';

interface ToastState {
  isOpen: boolean;
  type: ToastType;
  title: string;
  message: string;
  duration: number;
}

interface ShowToastOptions {
  title?: string;
  message: string;
  duration?: number;
}

interface UseToastReturn {
  toastState: ToastState;
  showSuccess: (options: ShowToastOptions | string) => void;
  showError: (options: ShowToastOptions | string) => void;
  showWarning: (options: ShowToastOptions | string) => void;
  showInfo: (options: ShowToastOptions | string) => void;
  closeToast: () => void;
}

const initialState: ToastState = {
  isOpen: false,
  type: 'info',
  title: '',
  message: '',
  duration: 4000
};

export function useToast(): UseToastReturn {
  const [toastState, setToastState] = useState<ToastState>(initialState);

  const closeToast = useCallback(() => {
    setToastState(prev => ({ ...prev, isOpen: false }));
  }, []);

  const showToast = useCallback((type: ToastType, options: ShowToastOptions | string) => {
    const opts = typeof options === 'string' 
      ? { message: options } 
      : options;

    setToastState({
      isOpen: true,
      type,
      title: opts.title || '',
      message: opts.message,
      duration: opts.duration || 4000
    });
  }, []);

  const showSuccess = useCallback((options: ShowToastOptions | string) => {
    showToast('success', options);
  }, [showToast]);

  const showError = useCallback((options: ShowToastOptions | string) => {
    showToast('error', options);
  }, [showToast]);

  const showWarning = useCallback((options: ShowToastOptions | string) => {
    showToast('warning', options);
  }, [showToast]);

  const showInfo = useCallback((options: ShowToastOptions | string) => {
    showToast('info', options);
  }, [showToast]);

  return {
    toastState,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    closeToast
  };
}

export default useToast;